import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { CustomEventNames, EventTypes, logRBIEvent } from 'features/analytics';
import { useActivatePromotion } from 'features/promotions/hooks/use-activate-promotion';
import { StyleSheet, View } from 'react-native';

import { PromotionActivationButton } from 'pages/loyalty/loyalty-promo-message-ribbon/promotion-activation-button';
import { ZIndex, primitive, spacing } from 'styles/constants';
import { WEB_APP_MAX_WIDTH_DESKTOP_LARGE } from 'utils/constants';
import logger from 'utils/logger';

interface PromotionMeta {
  name?: string | null;
  loyaltyEngineId?: string | null;
}

interface ActivatablePromoRibbonProps {
  message: React.ReactElement;
  loyaltyId: string;
  promotionId: string;
  userActivatedAt?: string | null;
  onActivationResolved: (result: { succeeded: boolean; error?: unknown }) => Promise<void>;
  autoActivate?: boolean;
  rounded?: boolean;
  promotionMeta?: PromotionMeta;
}

const logPromoActivatedEvent = (
  meta: PromotionMeta | undefined,
  status: 'Successful' | 'Failed'
) => {
  logRBIEvent({
    name: CustomEventNames.PROMO_ACTIVATED,
    type: EventTypes.Other,
    attributes: {
      name: meta?.name ?? undefined,
      loyaltyEngineId: meta?.loyaltyEngineId ?? undefined,
      status,
    },
  });
};

export const ActivatablePromoRibbon: React.FC<ActivatablePromoRibbonProps> = ({
  message,
  loyaltyId,
  promotionId,
  userActivatedAt,
  onActivationResolved,
  autoActivate,
  rounded = false,
  promotionMeta,
}) => {
  const { activatePromotion } = useActivatePromotion();
  const [isActivating, setIsActivating] = useState(false);
  const hasAutoActivatedRef = useRef(false);

  const handleActivatePromotion = useCallback(async () => {
    setIsActivating(true);

    let activationSucceeded = false;
    let activationError: unknown;
    try {
      await activatePromotion({ loyaltyId, promotionId });
      activationSucceeded = true;
    } catch (error) {
      activationError = error;
      logger.error({ error, message: 'Error activating promotion' });
    }

    logPromoActivatedEvent(promotionMeta, activationSucceeded ? 'Successful' : 'Failed');

    // Resolve after success or failure: on a rejection the authoritative re-read
    // drops the promotion so the ribbon clears instead of looping on failed taps.
    try {
      await onActivationResolved({ succeeded: activationSucceeded, error: activationError });
    } catch (error) {
      logger.error({
        error,
        message: 'Error in onActivationResolved callback after promotion activation',
      });
    }

    setIsActivating(false);
  }, [activatePromotion, loyaltyId, promotionId, onActivationResolved, promotionMeta]);

  useEffect(() => {
    if (autoActivate && !userActivatedAt && !hasAutoActivatedRef.current) {
      hasAutoActivatedRef.current = true;
      handleActivatePromotion();
    }
  }, [autoActivate, userActivatedAt, handleActivatePromotion]);

  const allowActivation = !userActivatedAt;

  return (
    <View
      style={[
        styles.ribbonContent,
        userActivatedAt ? styles.activatedContent : undefined,
        rounded && styles.rounded,
      ]}
    >
      <View style={styles.ribbonWrapper}>
        <View style={styles.textWrapper}>
          <Icon
            variant={userActivatedAt ? 'checkFill' : 'bkCrown'}
            size="$4"
            style={styles.promotionIcon}
            color={userActivatedAt ? primitive.$bbqBrown : primitive.$meltyYellow}
          />
          {message}
        </View>
        {allowActivation && (
          <PromotionActivationButton onPress={handleActivatePromotion} isLoading={isActivating} />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  ribbonContent: {
    zIndex: ZIndex.NORMAL,
    backgroundColor: primitive.$meltyYellowOpacity20,
    height: spacing.$10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rounded: {
    borderRadius: primitive.$borderRadius,
  },
  activatedContent: {
    backgroundColor: primitive.$successLight,
  },
  ribbonWrapper: {
    zIndex: ZIndex.MAX,
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
    gap: spacing.$2,
    width: '100%',
    maxWidth: WEB_APP_MAX_WIDTH_DESKTOP_LARGE,
    paddingHorizontal: spacing.$3,
  },
  textWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: spacing.$1,
    flex: 1,
  },
  promotionIcon: {
    width: primitive.fontSizes.md,
    flexShrink: 0,
  },
});
